import { isDemoMode } from 'src/api/state';
import type { LinkedPrincipalState } from 'src/app/useLinkedPrincipal';

type AccountGateInput = {
  /** `crm_enable_account_overview` from the company payload. */
  accountOverviewFlag?: boolean;
  /** `crm_enable_comms_preferences` from the company payload. */
  commsPreferencesFlag?: boolean;
  /** Whether this principal has a personal account — `useLinkedPrincipal()`. */
  principal?: LinkedPrincipalState;
};

/**
 * Whether the "My account" nav entry (and section) is available.
 *
 * WAITS ON: real `account/*` endpoints. Outside the demo build the account
 * overview's summary cards are hidden (they are fixture-backed — see
 * routes/account/exact), so the page is worth linking to only when
 * communication preferences (a real endpoint) is on. Otherwise the nav entry
 * would be a dead link to an empty screen.
 *
 * A principal with no personal account behind it — the anonymous
 * address-search login AND the machine PropertyUser the admin platform's
 * "Open portal" link mints — has nothing to show: every account surface
 * degrades to the "Sign in to manage your preferences" empty state. So gate on
 * the principal's linked-ness (`useLinkedPrincipal`), not on the tenant's
 * login method and not on the `anonymous` flag alone, which the machine
 * principal does not carry. `unknown` (probe in flight) stays hidden — the
 * entry appears once the answer is in, rather than flashing for a visitor who
 * must not have it.
 */
export const isAccountSectionEnabled = ({
  accountOverviewFlag,
  commsPreferencesFlag,
  principal,
}: AccountGateInput): boolean =>
  !!accountOverviewFlag &&
  (isDemoMode() || !!commsPreferencesFlag) &&
  principal === 'linked';
